import * as React from 'react';
import { FC } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ExtraItem } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { AutocompleteTextField, NumberInputField } from '../fields';
import { formatEuros } from '../../../shared/string';
import { IconButton } from '@mui/material';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { Delete } from '@material-ui/icons';
import AppButton from '../../AppButton';

export interface ExtrasListProps {
    id?: string;
    expenses: ExtraItem[];
    expensesOptions: Array<{ value: ExtraItem; label: string }>;
    readOnly: boolean;
    canSeePrices: boolean;
    onAdd: (expense: ExtraItem) => void;
    onDelete: (index: number) => void;
    onUpdate?: (index: number, expense: ExtraItem) => void;
    openAddNewPopup?: boolean;
    onOpenAddNewPopupChange?: (value: boolean) => void;
}

export const ExtrasList: FC<ExtrasListProps> = ({
    id = 'extras',
    expenses,
    expensesOptions,
    readOnly,
    canSeePrices,
    onAdd,
    onDelete,
    onUpdate,
    openAddNewPopup,
    onOpenAddNewPopupChange,
}) => {
    const t = useTranslations();
    const [extrasModal, setExtrasModal] = React.useState(false);
    const [selectedExpense, setSelectedExpense] = React.useState<ExtraItem | null>(null);
    const [quantity, setQuantity] = React.useState<number>(1);
    const [price, setPrice] = React.useState<number>(0);
    const [editingIndex, setEditingIndex] = React.useState<number | null>(null);

    // Watch for external requests to open the modal
    React.useEffect(() => {
        if (openAddNewPopup) {
            setExtrasModal(true);
            onOpenAddNewPopupChange?.(false);
        }
    }, [openAddNewPopup, onOpenAddNewPopupChange]);

    const handleCloseModal = () => {
        setExtrasModal(false);
        setSelectedExpense(null);
        setQuantity(1);
        setEditingIndex(null);
    };

    const handleSaveExpense = () => {
        if (selectedExpense) {
            const expenseWithQuantity = { ...selectedExpense, quantity, value: price };
            if (editingIndex !== null && onUpdate) {
                onUpdate(editingIndex, expenseWithQuantity);
            } else {
                onAdd(expenseWithQuantity);
            }
            handleCloseModal();
        }
    };

    const handleEditExpense = (index: number) => {
        if (readOnly) return;
        const expense = expenses[index];
        setSelectedExpense(expense);
        setQuantity(expense.quantity ?? 1);
        setEditingIndex(index);
        setExtrasModal(true);
    };

    const showSection = !!expenses.length || (!readOnly && !!expensesOptions.length);

    if (!showSection) {
        return null;
    }

    return (
        <>
            <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--spacer-XS)' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, marginRight: 'var(--spacer-XXS)' }}>
                        {t(TK.extras)}
                    </label>
                    {!readOnly && !!expensesOptions.length && (
                        <IconButton id={`${id}-add-extra-button`} size="small" onClick={() => setExtrasModal(true)}>
                            <AddCircleIcon />
                        </IconButton>
                    )}
                </div>
                {!!expenses.length && (
                    <div>
                        {expenses.map((expense, index) => (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: 'var(--spacer-XS)',
                                    borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
                                    cursor: readOnly ? 'default' : 'pointer',
                                    transition: 'background-color 0.2s',
                                }}
                                onClick={(e) => {
                                    // Don't trigger edit if clicking delete button
                                    if (!(e.target as HTMLElement).closest('button')) {
                                        handleEditExpense(index);
                                    }
                                }}
                                onMouseEnter={(e) => !readOnly && (e.currentTarget.style.backgroundColor = 'var(--bg-hover-light)')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                                <div>
                                    {!readOnly && (
                                        <AppButton
                                            color="link"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(index);
                                            }}
                                        >
                                            <Delete fontSize="small" color="error" />
                                        </AppButton>
                                    )}
                                    <span>
                                        {expense.name}
                                        {(expense.quantity ?? 1) > 1 && <span style={{color: 'var(--text-muted)'}}> × {expense.quantity}</span>}
                                    </span>
                                </div>
                                <span>{canSeePrices ? formatEuros(expense.value * (expense.quantity ?? 1)) : null}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Modal isOpen={extrasModal} toggle={handleCloseModal}>
                <ModalHeader toggle={handleCloseModal}>
                    {editingIndex !== null ? t(TK.edit) : t(TK.add)} {t(TK.extras)}
                </ModalHeader>
                <ModalBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
                        <AutocompleteTextField
                            id={`${id}-extra-autocomplete`}
                            label={t(TK.extras)}
                            value={selectedExpense?.name ?? ''}
                            options={expensesOptions.map(e => e.label)}
                            disabled={editingIndex !== null}
                            onValueChanged={(label) => {
                                const expense = expensesOptions.find(e => e.label === label)?.value;
                                if (expense) {
                                    setSelectedExpense(expense);
                                    setPrice(expense.value);
                                }
                            }}
                        />
                        {selectedExpense && <>
                            <div style={{ display: 'flex', gap: 'var(--spacer-S)' }}>
                                {canSeePrices && (
                                    <NumberInputField
                                        id={`${id}-price-input`}
                                        label={t(TK.price)}
                                        value={price}
                                        append='€'
                                        onChange={(value) => setPrice(value as number)}
                                />)}
                                <NumberInputField
                                    id={`${id}-quantity-input`}
                                    label={t(TK.quantity)}
                                    value={quantity}
                                    min={1}
                                    onChange={(value) => setQuantity(value as number)}
                                />
                            </div>
                            {canSeePrices && (
                                <div style={{ fontWeight: 500, textAlign: 'right' }}>
                                    {t(TK.total)}: {formatEuros(price * quantity)}
                                </div>
                            )}
                        </>}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <AppButton
                        id={`${id}-extra-save-button`}
                        color="success"
                        disabled={!selectedExpense}
                        onClick={handleSaveExpense}
                    >
                        {editingIndex !== null ? t(TK.save) : t(TK.add)}
                    </AppButton>
                    <AppButton id={`${id}-extra-cancel-button`} color="secondary" onClick={handleCloseModal}>
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ExtrasList;
